import { goBack } from "assets/icons";
import styled from "@emotion/styled";
import { Card, Col, Form, Row, Tabs, Typography, message } from "antd";
import { useNavigate } from "react-router-dom";
import { useGetMasterProvince } from "hooks/useMasterData";
import { FC, useEffect, useState } from "react";
import { useGetAgentById } from "hooks/useAgents";
import {
  useGetRetailById,
  useUpdateAgent,
  useUpdateRetail,
} from "hooks/useFactory";
import { AddressData } from "../CreateRetail/AddressComponents/interface/address.interface";
import MainCreateRetail from "../CreateRetail/RetailComponents/MainCreateRetail";
import MainCreateShippingOrder from "../CreateRetail/ShippingComponents/MainCreateShippingOrder";
import MainCreateBankingRetail from "../CreateRetail/BankingComponents/MainCreateBankingRetail";
import MainCreateRetailAddress from "../CreateRetail/AddressComponents/MainCreateRetailAddress";
import FooterCreateRetail from "../CreateRetail/FooterCreateRetail";
import MainCreateRetailOrder from "../CreateRetail/OrderComponents/MainCreateRetailOrder";
import MainCreateOrderTax from "components/OrderManage/CreateOrders/TaxSection/MainCreateOrderTax";
import { useLoadTaxInvoiceList } from "hooks/useOrders";
import {
  AgentByIdResponse,
  GetAgentByIdResponse,
  TaxAddressData,
} from "services/interface";
import { useAppSelector } from "store";
import MobileManage from "components/MobileManage";

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

interface UpdateRetailProps {
  id: string;
  section?: string;
  tabsKey?: string;
}

const UpdateRetail: FC<UpdateRetailProps> = ({ id, section, tabsKey }) => {
  const navigate = useNavigate();
  const factoryId = useAppSelector(
    (state) => state.auth.profile?.user?.role?.ref_id
  );
  const [dataAddress, setDataAddress] = useState<AddressData[]>([]);
  const {
    data: retailData,
    isLoading,
    refetch: refetchAgent,
    isError,
  } = useGetRetailById(id);
  const [userData, setUserData] = useState<GetAgentByIdResponse>();

  const [dataAddressTax, setDataAddressTax] = useState<TaxAddressData[]>([]);
  const [requestTax, setRequestTax] = useState<boolean>(false);
  const [addressIdSelected, setAddressIdSelected] = useState<string>("");
  const [secondaryMobile, setSecondaryMobile] = useState<string[]>([]);

  const {
    data: dataTax,
    isPending: taxPending,
    refetch: refetchTax,
  } = useLoadTaxInvoiceList({
    factory_id: factoryId ?? "",
    customer_id: id ?? "",
  });

  const [form] = Form.useForm();
  const { data: masterProviceData, isLoading: proviceLoading } =
    useGetMasterProvince();
  const { mutate: updateRetail } = useUpdateRetail();

  const handleRemoveItem = (target: string) => {
    const updatedDataAddress = dataAddress.filter(
      (item, index) => item.address_id !== target
    );
    setDataAddress(updatedDataAddress);
  };

  useEffect(() => {
    if (retailData) {
      setUserData(retailData);
    }
  }, [retailData]);

  useEffect(() => {
    if (userData) {
      form.setFieldsValue({
        firstname: userData.data.first_name || "",
        lastname: userData.data.last_name || "",
        telephone: userData.data.telephone_number || "",
        email: userData.data.email || "",
        datasource: userData.data.datasource || "",
        facebook: userData.data.facebook || "",
        is_vip: userData.data.is_vip || false,
        alias: userData.data.alias || "",
      });

      // Map address data
      const newAddresses: AddressData[] = userData.data.address_ship?.map(
        (address) => ({
          address_id: address.address_id,
          transport_type: address.transport_type,
          line_1: address.address_line_1,
          line_2: address.address_line_2,
          location_type: address.location_type,
          address_text: address.address_text,
          province_id: address.province_id,
          province_name: address.province_name,
          district_id: address.district_id,
          district_name: address.district_name,
          sub_district_id: address.sub_district_id,
          sub_district_name: address.sub_district_name,
          post_code: address.zip_code,
          address_type: address.address_type,
          address_name: address.address_name,
          address_telephone: address.address_telephone,
          address_remark: address.address_remark,
          is_default: address?.is_default,
          last_address: address?.last_address,
          parking_id: address?.parking_id,
        })
      );

      setDataAddress(newAddresses);
      setSecondaryMobile(userData.data.contact_mobile ?? []);
    }
  }, [userData, form]);

  useEffect(() => {
    if (id && dataTax?.data) {
      const newTaxAddresses: TaxAddressData[] = dataTax.data.map(
        (tax: TaxAddressData) => {
          return {
            id: tax.id,
            name: tax.name,
            address_text: tax.address_text,
            telephone: tax.telephone,
            fax: tax.fax,
            email: tax.email,
            tax_no: tax.tax_no,
            factory_id: factoryId ?? "",
            ref_id: id ?? "",
            ref_type: "agent",
            is_active: tax.is_active,
          };
        }
      );

      setDataAddressTax(newTaxAddresses);

      const activeAddress = newTaxAddresses.find((tax) => tax.is_active);
      if (activeAddress) {
        setAddressIdSelected(activeAddress.id);
      } else {
        setAddressIdSelected("");
      }
    }
  }, [dataTax]);

  const onFinish = (values: any) => {
    let addressData: AddressData | null = null;

    if (values.province_id) {
      addressData = {
        address_id: userData?.data?.address_contact[0]?.address_id,
        line_1: values.address_text,
        province_id: values.province_id,
        district_id: values.district_id,
        sub_district_id: values.sub_district_id,
        post_code: values.zip_code,
        address_type: "contact",
        transport_type: values.transport,
      };
    }

    const dataRaw: any = {
      id: userData?.data?.id,
      first_name: values.firstname,
      last_name: values.lastname,
      mobile: values.telephone,
      email: values.email,
      address: addressData ? addressData : null,
      datasource: values.datasource,
      facebook: values.facebook,
      ship_address: dataAddress,
      is_vip: values.is_vip,
      privilege_type: values.is_vip ? "vip" : null,
      alias: values.alias,
      contact_mobile: secondaryMobile,
    };

    updateRetail(dataRaw, {
      onSuccess: (res) => {
        if (res.status === "success") {
          message.success("แก้ไขลูกค้าสำเร็จ");

          setUserData((prevData) => {
            if (!prevData) return prevData;

            const updatedData: AgentByIdResponse = {
              ...prevData.data,
              is_vip: values.is_vip,
              id: prevData.data.id || "",
            };

            return {
              ...prevData,
              data: updatedData,
            };
          });
          refetchAgent();
        } else {
          message.error(`แก้ไขลูกค้าไม่สำเร็จ: ${res.error_message}`);
        }
      },
      onError: (error) => {
        message.error("แก้ไขลูกค้าไม่สำเร็จ");
      },
    });
  };

  const items = [
    {
      label: `ข้อมูลทั่วไป`,
      key: "0",
      children: (
        <>
          <MainCreateRetail
            form={form}
            masterProviceData={masterProviceData}
            dataAddress={userData?.data?.address_contact[0]}
          />
          <Divider />
          <MobileManage
            primaryMobile={userData?.data?.telephone_number ?? ""}
            secondaryMobile={secondaryMobile}
            setSecondaryMobile={setSecondaryMobile}
          />
          <Divider />
          <Form>
            <MainCreateOrderTax
              refetchData={refetchTax}
              dataAddressTax={dataAddressTax}
              setDataAddressTax={setDataAddressTax}
              refId={userData?.data.id ?? ""}
              refType={userData?.data.ref_type ?? ""}
              requestTax={requestTax}
              setRequestTax={setRequestTax}
              addressIdSelected={addressIdSelected}
              setAddressIdSelected={setAddressIdSelected}
            />
          </Form>
          <Divider />
          <MainCreateBankingRetail retailId={userData?.data?.id ?? ""} />
          <Divider />
          <MainCreateRetailAddress
            id={id}
            form={form}
            dataAddress={dataAddress}
            handleRemoveItem={handleRemoveItem}
            setDataAddress={setDataAddress}
            masterProviceData={masterProviceData}
            searchOption={false}
            canSort={false}
            refetchData={refetchAgent}
            personAddress={userData?.data?.address_contact[0]}
            addTemporary={false}
          />
          <FooterCreateRetail loading={proviceLoading} />
        </>
      ),
    },
    {
      label: `รายการคำสั่งซื้อ`,
      key: "1",
      children: (
        <>
          <MainCreateRetailOrder
            id={id}
            userData={userData}
            // setUserData={setUserData}
            dataAddress={dataAddress}
            canUpdate={false}
            canSort={true}
            tabsKey={tabsKey}
            refType={"retail"}
          />
        </>
      ),
    },
  ];
  return (
    <Form form={form} {...formItemLayout} onFinish={onFinish}>
      <Row className="rowgap-vbox" gutter={[24, 0]}>
        <Col span={24}>
          <Card bordered={false} className="criclebox h-full tabs-mobile">
            <HeaderTabs>
              <IconWrapper
                onClick={() => {
                  navigate("/manage-agent", { state: { fromDetail: true } });
                }}
              >
                <SpanIcon>{goBack}</SpanIcon>
              </IconWrapper>
              <Typography.Title level={5}>ข้อมูลลูกค้า</Typography.Title>
              <RightIconWrapper></RightIconWrapper>
            </HeaderTabs>
            <WrapperAll>
              <Tabs
                defaultActiveKey={section ? "1" : "0"}
                style={{ height: "100%" }}
                items={items}
                key="key"
              />
            </WrapperAll>
          </Card>
        </Col>
      </Row>
    </Form>
  );
};

export default UpdateRetail;

const Divider = styled.div`
  border-bottom: 0.5px solid #e8e8e8;
  margin: 10px 0px 10px 0px;
`;

const HeaderTabs = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  flex-direction: row;
  border-bottom: 1px solid #efefef;
`;

const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 30px;
  border-radius: 6px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    background-color: #efefef;
  }
`;

const SpanIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const WrapperAll = styled.div`
  display: block;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;
